/**
 * Mega Menu Interactions
 *
 * Handles Bootstrap dropdown integration and custom behaviors for mega menus
 * - Bootstrap dropdown handles click toggle, outside-click close, and Escape close
 * - Manages ARIA states for accessibility
 * - Custom mobile slide-in behavior (bypasses Bootstrap dropdown)
 * - Integrates with mobile navigation toggle
 */
export default class MegaMenu {
    constructor() {
        this.megaMenus = $('.main-nav__mega-menu');

        if (this.megaMenus.length === 0) {
            return; // No mega menus on this page
        }

        this.megaMenuData = [];

        this.megaMenus.each((index, megaMenu) => {
            const $megaMenu = $(megaMenu);
            const $parentItem = $megaMenu.closest('.main-nav__item.dropdown');
            const $parentLink = $parentItem.find('.main-nav__link');
            const $button = $parentItem.find('.main-nav__button');

            // Add "Back to Main Menu" button at the top of mega menu for mobile
            const backButton = $('<button class="main-nav__mega-menu__back" type="button">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M19 12H5M12 19l-7-7 7-7"/>' +
                '</svg>' +
                '<span>Back to Main Menu</span>' +
                '</button>');

            // Add close button for desktop
            const closeButton = $('<button class="main-nav__mega-menu__close" type="button" aria-label="Close menu">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">' +
                '<g clip-path="url(#a)"><path fill="currentColor" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></g>' +
                '<defs><clipPath id="a"><path fill="currentColor" d="M0 0h24v24H0z"/></clipPath></defs>' +
                '</svg>' +
                '</button>');

            // Insert back button at the beginning of the mega menu wrapper
            const $wrapper = $megaMenu.find('.main-nav__mega-menu__wrapper');
            $wrapper.prepend(backButton);
            $megaMenu.find('.main-nav__mega-menu__container').append(closeButton);

            this.megaMenuData.push({
                megaMenu: $megaMenu,
                parentItem: $parentItem,
                parentLink: $parentLink,
                button: $button,
                backButton: backButton,
                closeButton: closeButton,
            });
        });

        // Track current breakpoint state
        this.isDesktop = window.matchMedia('(min-width: 992px)').matches;

        this.init();
    }

    init() {
        this.manageAriaState();
        this.handleDesktopClick();
        this.handleCloseButton();
        this.handleMobileInteractions();
        this.handleMobileNavToggle();
        this.handleBreakpointChange();
    }

    /**
     * Update aria-expanded and manage body scroll lock when dropdown events fire
     * Bootstrap dropdown fires events on the parent .dropdown element
     */
    manageAriaState() {
        const desktopBreakpoint = window.matchMedia('(min-width: 992px)');

        this.megaMenuData.forEach(({parentItem, megaMenu, button}) => {
            parentItem.on('show.bs.dropdown', () => {
                button.attr('aria-expanded', 'true');

                if (desktopBreakpoint.matches) {
                    $('body').addClass('mega-menu-open');

                    // Position decorative arrow under the parent menu item
                    const itemRect = parentItem[0].getBoundingClientRect();
                    const arrowLeft = itemRect.left + itemRect.width / 2;
                    megaMenu[0].style.setProperty('--mega-menu-arrow-left', arrowLeft + 'px');
                }
            });

            parentItem.on('hide.bs.dropdown', () => {
                button.attr('aria-expanded', 'false');
            });

            parentItem.on('hidden.bs.dropdown', () => {
                if ($('.main-nav__mega-menu.show').length === 0) {
                    $('body').removeClass('mega-menu-open');
                }
            });
        });
    }

    /**
     * Handle click behavior on desktop
     * First click on parent link opens mega menu
     * Second click (when mega menu is open) follows the link to navigate
     */
    handleDesktopClick() {
        const desktopBreakpoint = window.matchMedia('(min-width: 992px)');

        this.megaMenuData.forEach(({megaMenu, parentLink, button}) => {
            parentLink.on('click', (e) => {
                if (desktopBreakpoint.matches) {
                    if (megaMenu.hasClass('show')) {
                        // Mega menu is already open — allow navigation
                        return;
                    }
                    // Mega menu is closed — open it and prevent navigation
                    e.preventDefault();
                    e.stopPropagation(); // Prevent Bootstrap's document click from immediately closing
                    button.dropdown('toggle');
                }
            });
        });
    }

    /**
     * Handle close button click to dismiss mega menu
     */
    handleCloseButton() {
        this.megaMenuData.forEach(({closeButton, button}) => {
            closeButton.on('click', () => {
                button.dropdown('toggle');
                button.focus();
            });
        });
    }

    /**
     * Handle mobile mega menu interactions
     * - Parent link / button click opens mega menu (slides in from right)
     * - Back button closes mega menu and returns to main menu
     * - Prevents Bootstrap dropdown from firing on mobile
     */
    handleMobileInteractions() {
        const mobileBreakpoint = window.matchMedia('(max-width: 991px)');

        this.megaMenuData.forEach(({megaMenu, parentLink, button, backButton}) => {
            const openMegaMenu = (e) => {
                if (mobileBreakpoint.matches) {
                    e.preventDefault();
                    e.stopPropagation();

                    // Close all other mega menus first
                    this.megaMenus.each(function() {
                        $(this).removeClass('show mobile-open');
                    });

                    // Open this mega menu
                    megaMenu.addClass('show mobile-open');

                    // Hide main menu
                    $('.main-nav').addClass('mega-menu-active');
                }
            };

            parentLink.on('click', openMegaMenu);
            button.on('click', openMegaMenu);

            backButton.on('click', () => {
                if (mobileBreakpoint.matches) {
                    megaMenu.removeClass('show mobile-open');
                    $('.main-nav').removeClass('mega-menu-active');
                }
            });
        });
    }

    /**
     * Close mega menus when mobile navigation is toggled off
     */
    handleMobileNavToggle() {
        $('#mobile-nav-button').on('click', () => {
            if (!$('body').hasClass('nav-is-open')) {
                // Close all mega menus
                this.megaMenus.removeClass('show mobile-open');
                $('.main-nav').removeClass('mega-menu-active');
                $('body').removeClass('mega-menu-open');
            }
        });
    }

    /**
     * Handle breakpoint changes (mobile ↔ desktop)
     * Resets all mega menu states when switching
     */
    handleBreakpointChange() {
        const desktopBreakpoint = window.matchMedia('(min-width: 992px)');

        let resizeTimeout;

        $(window).on('resize', () => {
            clearTimeout(resizeTimeout);

            resizeTimeout = setTimeout(() => {
                const nowDesktop = desktopBreakpoint.matches;

                if (this.isDesktop !== nowDesktop) {
                    // Close all mega menus — remove dropdown .show from menus and parent items
                    this.megaMenus.removeClass('show mobile-open');
                    this.megaMenuData.forEach(({parentItem, button}) => {
                        parentItem.removeClass('show');
                        button.attr('aria-expanded', 'false');
                    });

                    // Reset main menu visibility
                    $('.main-nav').removeClass('mega-menu-active');
                    $('body').removeClass('nav-is-open mega-menu-open');

                    this.isDesktop = nowDesktop;
                }
            }, 150);
        });
    }
}



// WEBPACK FOOTER //
// ./scripts/components/mega-menu.js